// TYPE-ONLY on purpose: the provenance DTO is declared beside the badge union it
// belongs to, and a type import is erased, so re-exporting this module from that
// same barrel creates no runtime cycle.
import type { AskHelixFigureProvenanceDto } from '../../ask-helix';

// ─────────────────────────────────────────────────────────────────────────────
// RANKED BREAKDOWN — THE LEADERBOARD BLOCK, ON THE SAME THREE RULES (#6164).
//
// A read that already publishes a governed figure PER GROUP (cost by function,
// headcount by team, margin by lane) owes the reader the ORDER those figures
// stand in, not just the figures. Until now that order reached glass only as
// prose or as table rows the reader ranks by eye.
//
// This is that block, on the metric-series doctrine without exception: the
// producer names the rows, their labels and their ORDER — server order IS the
// ranking, the reader derives rank from index and never re-sorts — and every
// figure a row carries is a `placeholder` into an engine-bound
// `numericBindings[]` entry on the same envelope. The producer states no number
// of its own, so the anti-fabrication fence sees exactly the numbers it already
// sees. A share is a governed ratio the producer PUBLISHED as its own binding
// and names via `sharePlaceholder` — the manager never divides.
//
// The public block is minted from the spec by the manager's ONE display
// authority (`managerDisplayValue`), so a row's `displayValue` is the same
// string the bindings table prints for the identical fact.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What the reader may say about a row's disposition. Closed, because the
 * surface styles on it and an open string would silently fall through.
 */
export const ASK_HELIX_RANKED_BREAKDOWN_STATUSES = Object.freeze([
  'taken',
  'held',
  'open',
] as const);

export type AskHelixRankedBreakdownStatusDto =
  (typeof ASK_HELIX_RANKED_BREAKDOWN_STATUSES)[number];

/** One row of the PRODUCER'S spec: a group label and the figure that ranks it. */
export type GovernedRankedBreakdownItemSpec = {
  /** Reader-facing group label, producer-authored ("Branch & Shared Ops"). */
  label: string;
  /**
   * The engine-bound `numericBindings[]` placeholder carrying this row's
   * figure. The producer publishes the binding; this only names it.
   */
  placeholder: string;
  /**
   * OPTIONAL governed share for this row — a placeholder into a binding the
   * producer already published as a ratio. Never a raw number, and never
   * derived by the manager: division here would be a second numeric authority.
   */
  sharePlaceholder?: string;
  status?: AskHelixRankedBreakdownStatusDto;
};

/**
 * The producer's breakdown spec, carried on the governed tool output envelope.
 * Reader-safe strings and placeholders only — it holds no figure at all.
 * Item order is the RANKING AUTHORITY; the renderer derives position from it.
 */
export type GovernedRankedBreakdownSpec = {
  /** Producer-stable identity for this breakdown ("function_cost_rollup"). */
  breakdownId: string;
  /** What is ranked, in the reader's words. */
  title: string;
  unit: string | null;
  /** Ranked, producer order; the renderer never re-sorts. */
  items: readonly GovernedRankedBreakdownItemSpec[];
  /** The governed source class, copied from the producer's own declaration. */
  sourceClassLabel: string | null;
  // NO PROSE CONCLUSION — same rule as the series spec: a producer with a
  // sentence about the ranking says it as narration input to the one narrator.
};

/**
 * A published row. `displayValue` is the ONLY string a surface prints for this
 * figure, and `value` is GEOMETRY — it sizes the bar, never formats.
 *
 * A row whose placeholder resolved to no single governed fact publishes
 * `displayValue: null` with no `value` — a disclosed absence, never a zero.
 * A `sharePlaceholder` that resolved to nothing publishes no `share` at all.
 */
export type AskHelixRankedBreakdownItemDto = {
  label: string;
  displayValue: string | null;
  /** Omitted, never null and never non-finite, when there is nothing to size. */
  value?: number;
  share?: { displayValue: string };
  status?: AskHelixRankedBreakdownStatusDto;
  /** The badge of the binding this row was minted from. */
  provenance?: AskHelixFigureProvenanceDto;
};

/**
 * The public leaderboard block. Present only when a producer emitted a spec;
 * OMITTED, never null.
 */
export type AskHelixRankedBreakdownDto = {
  breakdownId?: string;
  title: string;
  unit: string | null;
  /** Server order = rank; the web reader derives `position` from index. */
  items: AskHelixRankedBreakdownItemDto[];
  /**
   * FALLBACK ONLY — the per-row badge is the truth. Carries the WEAKEST class
   * present so a one-chip renderer understates rather than overstates.
   */
  provenance: AskHelixFigureProvenanceDto | null;
  sourceClassLabel: string | null;
};
